'use client';

import { useState } from 'react';
import type { JSX } from 'react';
import dynamic from 'next/dynamic';
import Typography from '@alltrails/core/components/Typography';
import { FormattedMessage, useIntl } from 'react-intl';
import ThumbsUp from '@alltrails/denali/icons/ThumbsUp';
import ThumbsUpFilled from '@alltrails/denali/icons/ThumbsUpFilled';
import logReviewHelpfulVoteUpdated from '@alltrails/amplitude/events/logReviewHelpfulVoteUpdated';
import useUser from '@alltrails/context/hooks/useUser';
import type { Review } from '@alltrails/types/Review';
import { useVoteReviewMutation } from '../../slices/reviewsApi';
import { getRemoveVoteLink, getUpvoteLink } from '../../utils/getVoteLinks';
import styles from './ReviewActionSection.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type ReviewVoteButtonProps = {
  review: Review;
};

const ReviewVoteButton = ({ review }: ReviewVoteButtonProps): JSX.Element | null => {
  const intl = useIntl();
  const user = useUser();
  const [voteReview, { isLoading: isVoting }] = useVoteReviewMutation();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);

  const upvoteLink = getUpvoteLink(review);
  const removeVoteLink = getRemoveVoteLink(review);

  const isUpvoted = review.votes?.myVote?.type === 'upvote';
  const upvoteCount = review.votes?.upvoteCount ?? (review as { upvote_count?: number }).upvote_count ?? 0;

  const canVote = !!review.votes?.links?.length;

  const handleUpvote = () => {
    if (!user) {
      setIsSignUpModalOpen(true);
      return;
    }
    if (isVoting || !canVote) return;
    const voteAction = isUpvoted ? 'remove' : 'add';

    if (isUpvoted && removeVoteLink) {
      voteReview({ link: removeVoteLink, reviewId: review.id });
    } else if (upvoteLink) {
      voteReview({ link: upvoteLink, reviewId: review.id });
    }

    logReviewHelpfulVoteUpdated({
      review_id: String(review.id),
      trail_id: String(review.trailId ?? ''),
      vote_action: voteAction
    });
  };

  if (!canVote) {
    return null;
  }

  const likeLabel = isUpvoted
    ? intl.formatMessage({ defaultMessage: 'Unmark as helpful' })
    : intl.formatMessage({ defaultMessage: 'Mark as helpful' });

  return (
    <div className={styles.voteSection}>
      <button
        className={styles.likeButton}
        onClick={handleUpvote}
        data-testid={`review-${review.id}-like-button`}
        type="button"
        title={likeLabel}
        aria-label={likeLabel}
        disabled={isVoting}
      >
        {isUpvoted ? (
          <ThumbsUpFilled data-testid={`review-${review.id}-thumbs-up-filled`} />
        ) : (
          <ThumbsUp color="--color-text-secondary" data-testid={`review-${review.id}-thumbs-up`} />
        )}
        <Typography variant="text200bold" color="secondary">
          <FormattedMessage defaultMessage="Helpful ({upvoteCount})" values={{ upvoteCount }} />
        </Typography>
      </button>
      {isSignUpModalOpen && (
        <SignUpModal
          autoClose
          closeModal={() => setIsSignUpModalOpen(false)}
          isOpen={isSignUpModalOpen}
          onSuccess={() => setIsSignUpModalOpen(false)}
          trigger="Review Trail"
          upsellTrigger="Review Trail"
        />
      )}
    </div>
  );
};

export default ReviewVoteButton;
